import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/data/categories';

const languages = [
  { code: 'pt', label: 'pt' },
  { code: 'de', label: 'de' },
  { code: 'en', label: 'en' },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = (i18n.language || 'en').toLowerCase().split('-')[0];
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileMenuOpen(false);
  };

  const goHome = () => {
    setMobileMenuOpen(false);
    navigate('/');
    window.scrollTo(0, 0);
  };

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const retry = document.getElementById(id);
        if (retry) retry.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md transition-shadow duration-300 ${
        scrolled ? 'shadow-md border-b border-gray-100' : 'border-b border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => window.scrollTo(0, 0)}>
            <span className="text-lg font-bold tracking-tight text-gray-900">
              CSSBuy <span className="text-green-600">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              onClick={() => window.scrollTo(0, 0)}
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors whitespace-nowrap"
            >
              {t('navHome')}
            </Link>
            <Link
              to="/article"
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors whitespace-nowrap"
            >
              {t('navShoppingGuide')}
            </Link>
            <button
              onClick={() => scrollToSection('pillar-guides')}
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors whitespace-nowrap"
            >
              {t('navGuides')}
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors whitespace-nowrap"
            >
              {t('navFAQ')}
            </button>

            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setLangOpen(false);
                }}
                className="text-sm font-medium flex items-center gap-1 text-gray-700 hover:text-green-600 transition-colors whitespace-nowrap"
              >
                {t('categoriesDropdown')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-100 p-2 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.key}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <img src={cat.img} alt={t(cat.key)} className="w-8 h-8 rounded-md object-cover shrink-0" />
                        <span className="text-sm text-gray-700 whitespace-nowrap">{t(cat.key)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => {
                  setLangOpen(!langOpen);
                  setDropdownOpen(false);
                }}
                className="text-sm font-medium flex items-center gap-1 text-gray-700 hover:text-green-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-global-line" />
                {t(currentLang)}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray-100 py-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${currentLang === lang.code ? 'text-green-600 font-medium' : 'text-gray-700'}`}
                    >
                      {t(lang.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-900 transition-colors"
            aria-label="Menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Primary actions */}
            <Link
              to="/"
              onClick={goHome}
              className="flex items-center gap-3 px-3 py-3 rounded-lg bg-green-50 text-green-700 font-semibold"
            >
              <i className="ri-home-4-line text-lg" />
              {t('navHome')}
            </Link>
            <Link
              to="/article"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg bg-gray-50 text-gray-800 font-semibold"
            >
              <i className="ri-shopping-bag-3-line text-lg" />
              {t('navShoppingGuide')}
            </Link>
            <button
              onClick={() => scrollToSection('pillar-guides')}
              className="flex items-center gap-3 px-3 py-3 rounded-lg w-full text-left text-gray-800 font-medium"
            >
              <i className="ri-book-open-line text-lg" />
              {t('navGuides')}
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="flex items-center gap-3 px-3 py-3 rounded-lg w-full text-left text-gray-800 font-medium"
            >
              <i className="ri-question-line text-lg" />
              {t('navFAQ')}
            </button>

            {/* Categories accordion */}
            <div className="border-t border-gray-100 pt-2 mt-1">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-gray-800 font-semibold"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg" />
                  {t('categoriesTitle')}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-2 px-3 pb-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 rounded-lg bg-gray-50 p-2 hover:bg-gray-100 transition-colors"
                    >
                      <img src={cat.img} alt={t(cat.key)} className="w-9 h-9 rounded-md object-cover shrink-0" />
                      <span className="text-xs font-medium text-gray-700 leading-tight">{t(cat.key)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Language */}
            <div className="border-t border-gray-100 pt-2">
              <p className="px-3 text-xs font-medium text-gray-400 mb-1">{t('language')}</p>
              <div className="flex gap-2 px-3">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${currentLang === lang.code ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    {t(lang.label)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}